import { FileText, Loader2 } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

import { type OutreachDetailChrome } from '@apps/web/components/outreaches/outreach_detail_chrome';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import {
    type JoinedOutreachRead,
    type Shift,
} from '@libraries/typescript/api_client';

type MissingCarePlanShift = Pick<Shift, 'id' | 'client_id' | 'patient_name'>;

type ClientGroup = {
    readonly key: string;
    readonly clientId: string | null;
    readonly patientName: string;
    readonly shiftIds: ReadonlyArray<string>;
};

type MissingCarePlanBannerProps = {
    // Present on the outreach detail page; absent in the create-outreach
    // composer where the outreach does not exist yet. When present, the
    // generated summary is also copied into the outreach's per-shift
    // metadata so the first messages use it immediately.
    readonly outreach?: JoinedOutreachRead;
    readonly missingShifts: ReadonlyArray<MissingCarePlanShift>;
    readonly organizationId: string | null;
    // Receives the generated per-shift plans so the caller can embed them into
    // outreach metadata (composer) or merge into existing metadata (detail page).
    readonly onSaved: (
        plans: Record<
            string,
            { readonly summary: string; readonly bullets: string }
        >,
    ) => void | Promise<void>;
    readonly chrome?: OutreachDetailChrome;
    readonly className?: string;
};

/**
 * Warning shown when one or more shifts have no care plan data anywhere (no
 * raw client care plan, no saved client summary, no per-outreach summary).
 * Without it, SMS goes out with an empty care plan section and voice has
 * nothing to discuss. The CTA captures a quick coordinator-written summary,
 * generates the text bullets + voice summary from it, and saves the summary
 * on the client record so future outreaches for the same client reuse it.
 *
 * Rendered both in the create-outreach composer (pre-send, primary capture
 * point) and on the pending outreach detail page (fallback for outreaches
 * created outside the composer, e.g. agent/automation/API).
 */
export function MissingCarePlanBanner({
    outreach,
    missingShifts,
    organizationId,
    onSaved,
    chrome = 'default',
    className,
}: MissingCarePlanBannerProps) {
    const { formatName } = useFormatPersonName();
    const [open, setOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [drafts, setDrafts] = useState<Record<string, string>>({});

    const clientGroups = useMemo<ReadonlyArray<ClientGroup>>(() => {
        const groups = new Map<
            string,
            {
                clientId: string | null;
                patientName: string;
                shiftIds: Array<string>;
            }
        >();
        for (const shift of missingShifts) {
            const key = shift.client_id ?? `name:${shift.patient_name}`;
            const existing = groups.get(key);
            if (existing) {
                existing.shiftIds.push(shift.id);
            } else {
                groups.set(key, {
                    clientId: shift.client_id ?? null,
                    patientName: shift.patient_name,
                    shiftIds: [shift.id],
                });
            }
        }
        return Array.from(groups.entries()).map(([key, group]) => ({
            key,
            ...group,
        }));
    }, [missingShifts]);

    if (missingShifts.length === 0) {
        return null;
    }

    const clientNames = clientGroups
        .map((group) => formatName(group.patientName))
        .join(', ');
    const isCompact = chrome === 'compact';
    const hasAnyDraft = clientGroups.some(
        (group) => !!drafts[group.key]?.trim(),
    );

    const handleSave = async () => {
        if (!organizationId) {
            return;
        }
        setIsSaving(true);
        try {
            // Phase 1: generate summaries for every group that has a draft.
            // Collect results before writing anything so a failure in one
            // group doesn't leave partial state where some clients are
            // persisted but the outreach patch and onSaved never ran.
            type GroupResult = {
                readonly group: ClientGroup;
                readonly summary: string;
                readonly bullets: string;
                readonly perShiftPlans: Record<
                    string,
                    { summary: string | null; bullets: string | null }
                >;
            };
            const groupResults: Array<GroupResult> = [];
            for (const group of clientGroups) {
                const sourceText = drafts[group.key]?.trim();
                if (!sourceText) {
                    continue;
                }
                const response = await getApiClient().post({
                    url: '/organizations/{organization_id}/shifts/summarize-care-plan',
                    pathParams: { organization_id: organizationId },
                    body: {
                        shift_ids: [...group.shiftIds],
                        source_text: sourceText,
                    },
                    reason: 'missing-care-plan-banner',
                });
                const perShiftPlans: Record<
                    string,
                    { summary: string | null; bullets: string | null }
                > = {};
                for (const item of response.per_shift ?? []) {
                    perShiftPlans[item.shift_id] = {
                        summary: item.summary || null,
                        bullets: item.care_plan_bullets || null,
                    };
                }
                groupResults.push({
                    group,
                    summary: response.summary,
                    bullets: response.care_plan_bullets,
                    perShiftPlans,
                });
            }

            // Phase 2: persist — client records first, then outreach metadata.
            // All summarize calls succeeded before we write anything.
            const newShiftCarePlans: Record<
                string,
                { summary: string | null; bullets: string | null }
            > = {};
            for (const {
                group,
                summary,
                bullets,
                perShiftPlans,
            } of groupResults) {
                Object.assign(newShiftCarePlans, perShiftPlans);
                // Persist on the client record (ui source survives EHR syncs)
                // so future outreaches for this client reuse it.
                if (group.clientId) {
                    await getApiClient().patch({
                        url: '/organizations/{organization_id}/clients/{client_id}',
                        pathParams: {
                            organization_id: organizationId,
                            client_id: group.clientId,
                        },
                        body: {
                            care_plan_summary: { summary, bullets },
                            care_plan_summary_source: 'ui',
                        },
                        reason: 'missing-care-plan-banner',
                    });
                }
            }

            // On the detail page, copy generated summaries into the outreach's
            // per-shift metadata so the first messages use them immediately.
            // In the composer the outreach doesn't exist yet — the saved client
            // summary is picked up when the outreach is created via onSaved.
            if (outreach) {
                const existingShiftCarePlans = (outreach.outreach_metadata?.[
                    'shift_care_plans'
                ] ?? {}) as Record<
                    string,
                    { summary?: string | null; bullets?: string | null }
                >;
                await getApiClient().patch({
                    url: '/organizations/{organization_id}/callouts/{callout_id}',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                    },
                    body: {
                        shift_care_plans: {
                            ...existingShiftCarePlans,
                            ...newShiftCarePlans,
                        },
                    },
                    reason: 'missing-care-plan-banner',
                });
            }

            await onSaved(
                Object.fromEntries(
                    Object.entries(newShiftCarePlans).map(([id, plan]) => [
                        id,
                        {
                            summary: plan.summary ?? '',
                            bullets: plan.bullets ?? '',
                        },
                    ]),
                ),
            );
            toast.success('Care plan summary added', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            setOpen(false);
        } catch (error) {
            console.error('Failed to save care plan summary:', error);
            toast.error('Failed to save care plan summary', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Fragment>
            <div
                className={cn(
                    isCompact
                        ? 'flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5'
                        : 'flex items-center justify-between gap-4 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 p-3',
                    className,
                )}
                role="note"
                data-testid="missing-care-plan-banner"
            >
                <div
                    className={cn(
                        'flex items-center',
                        isCompact ? 'gap-2' : 'gap-2.5',
                    )}
                >
                    <FileText
                        className={
                            isCompact
                                ? 'h-3.5 w-3.5 flex-shrink-0 text-amber-700'
                                : 'h-4 w-4 flex-shrink-0 text-amber-600'
                        }
                        aria-hidden={true}
                    />
                    {isCompact ? (
                        <p className="text-xs text-amber-950">
                            No care plan on file for {clientNames}
                        </p>
                    ) : (
                        <div>
                            <p className="text-sm font-medium text-amber-900">
                                No care plan on file for {clientNames}
                            </p>
                            <p className="text-sm text-amber-700">
                                Add a quick summary so Phoebe has care details
                                to share during outreach.
                            </p>
                        </div>
                    )}
                </div>
                <Button
                    type="button"
                    variant={isCompact ? 'outline' : 'default'}
                    size="sm"
                    onClick={() => setOpen(true)}
                    className={
                        isCompact
                            ? 'h-7 flex-shrink-0 px-2.5 text-xs'
                            : 'flex-shrink-0'
                    }
                >
                    Add care plan summary
                </Button>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add care plan summary</DialogTitle>
                        <DialogDescription>
                            Write a few sentences about the client&rsquo;s care.
                            Phoebe will generate the text bullets and voice
                            summary from it and save it to the client.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {clientGroups.map((group) => (
                            <div key={group.key} className="space-y-1">
                                <p className="text-sm font-medium">
                                    {formatName(group.patientName)}
                                </p>
                                <Textarea
                                    value={drafts[group.key] ?? ''}
                                    onChange={(event) =>
                                        setDrafts((prev) => ({
                                            ...prev,
                                            [group.key]: event.target.value,
                                        }))
                                    }
                                    placeholder="e.g. Needs help with bathing and meal prep. Uses a walker. Two cats at home."
                                    rows={4}
                                    disabled={isSaving}
                                />
                            </div>
                        ))}
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setOpen(false)}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={isSaving || !hasAnyDraft}
                            >
                                {isSaving ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : null}
                                Generate &amp; Save
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </Fragment>
    );
}
